import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useTableNavigation from '../../../../ui/tables/useTableNavigation';

import { ApplicableTableSettings, codesPortCodeList } from '../../../../tableSettings';

import {
  NumberedIndicatorRenderer,
  TextAlignLeftRenderer,
  TextRenderer,
} from '../../../../uiControls/SmartTableRenderers';

import SmartTable from '../../../../uiControls/SmartTable';

import useFastStorage, { useTypedFastStorage } from '../../../../hooks/useFastStorage';
import useFunction from '../../../../hooks/useFunction';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';

import ApplicationContext from '../../../../ApplicationContext';
import SecondaryButton from '../../../../ui/SecondaryButton';
import CyLocationLookupField from '../../../../uiControls/lookup/CyLocationLookupField';
import { Search, isFilterObjectEmpty, resetSearchKeepPaging } from '../../../../filtering';
import { JobNames } from '../../../../framework.core/JobNames';
import { CodeScheduleDKListItem } from '../../../../fms.data/models/CodeScheduleDKListItem';
import { ApiResult, InsertResult } from '../../../../framework.core/data/ApiResult';
import { SQLType } from '../../../../framework.core/data/SQLInfo';
import { CheckLookup } from '../../../../fms.data/lookup/CheckLookup';
import { TextValue } from '../../../../framework.core/data/TextValue';
import InlineInputControlRow from '../../../../ui/layout/InlineInputControlRow';
import TextBox from '../../../../ui/TextBox';
import IfmsModal from '../../../../ui/IfmsModal';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import { FmsSecTask } from '../../../../fms.core/constants';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import { findSecurityTask } from '../../../../util';
import useFilterBar from '../../../../hooks/useFilterBar';
import { ListOrderBy } from "../../../common/AbstractListView";
import postal from 'postal';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import { Portal } from 'react-portal';
import CloseButton from '../../../../ui/CloseButton';
import { AddIconButton3, DeleteIconButton3, EditIconButton3, ViewIconButton3, RefreshIconButton2 } from '../../../../ui/IconButton';
import useDirtyCheck from '../../../../hooks/useDirtyCheck2';

function SdpDetails({
  readonly,
  sdp,
  onChange
}:{
  readonly: boolean,
  sdp: CodeScheduleDKListItem,
  onChange: (change: Partial<CodeScheduleDKListItem>) => void
}) {

  useEffect(() => {
    if (readonly) {
      return;
    } 
    
    const focusElement = document.getElementById('scheduledk-code');
    focusElement?.focus();
  }, [readonly]);

  return (
    <div>
      <div style={{display: 'flex'}}>
        <div style={{width: '320px'}}>
          <InlineInputControlRow label={<span>Schedule D&K Code <span style={{ color: 'red' }}>*</span></span>} mode="w115">
            <div style={{width: '100px'}}>
              <TextBox name='scheduledk-code' textId="scheduledk-code" disabled={readonly} maxLength = {10} value={sdp.code} onChange={e => onChange({code: e.target.value})} />
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow label="Port Name" mode="w115">
            <TextBox name='scheduledk-portname' disabled={readonly} maxLength = {50} value={sdp.portName} onChange={e => onChange({portName: e.target.value})} />
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '320px'}}>
          <InlineInputControlRow label="CY Location" mode="smaller-2x">
            <CyLocationLookupField
              maxLength={50}
              disabled={readonly}
              name='scheduledk-cy'
              lookupId="ifms-sdk-cy"
              initialValue={sdp.cyName ?? ''}
              onTextChange={cyName => onChange({
                cy: 0,
                cyName: cyName ?? '',
              })}
              onSelect={cyLocation => {
                onChange({
                  cy: cyLocation.id,
                  cyName: cyLocation.shortName ?? '',
                })
              }}
            />
          </InlineInputControlRow>

          <InlineInputControlRow label="CFS Location" mode="smaller-2x">
            <CyLocationLookupField
              maxLength={50}
              disabled={readonly}
              name='scheduledk-cfs'
              lookupId="ifms-sdk-cfs"
              initialValue={sdp.cfsName ?? ''}
              onTextChange={cyName => onChange({
                cfs: 0,
                cfsName: cyName ?? '',
              })}
              onSelect={cyLocation => {
                onChange({
                  cfs: cyLocation.id,
                  cfsName: cyLocation.shortName ?? '',
                })
              }}
            />
          </InlineInputControlRow>

        </div>
      </div>
    </div>
  );
}

function formatElement(data: any, index: number, blockStartPage: number, pageSize: number) {
  const indicator = (blockStartPage - 1) * pageSize + (index + 1);

  return {
    ...data,
    companyName: data.company?.shortName ?? '',
    indicator
  };
}

function formatData(data: any, pageNum: number, pageSize: number, pagePerBatch: number) {
  if (!data) return;

  // 블록의 시작 페이지 계산
  const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);

  return data.map((d: any, i: number) => formatElement(d, i, blockStartPage, pageSize));
}

function ScheduleDkPort({
  componentIndex
}: {
  componentIndex: number
}) {
  const pageSize: number = 25;
  const pagePerBatch: number = 10;

  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
  }});
  useFunctionKeys({onEsc: cancelOperation});
  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const {selectTableRow, tableNavigationCleanup } = useTableNavigation('codes-sdk-list', onRowSelect, onRowDoubleSelect, ['code'], false, false, false);
  const [pageNum, setPageNum, deletePageNum, pageNumRef] = useTypedFastStorage<number>(`codes-sdk-list-pageum`, 1);

  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, dataRef] = useFastStorage(`ifms-referenceFiles-sdk-list`, []);
  const [sdp, _setSdp, removeSdpFromStorage] = useTypedFastStorage<CodeScheduleDKListItem>(`ifms-referenceFiles-sdk-editable`, null);
    const {setCleanState, dirtyCleanup} = useDirtyCheck(`ifms-referenceFiles-sdk-editable`, ['edit', 'view']);
  
  const [orderBy, setOrderBy] = useTypedFastStorage<ListOrderBy>(`ifms-referenceFiles-sdk-orderby`, getDefaultOrderBy());
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeOceanPort);
  const [selectedRow, setSelectedRow] = useState(-1);
  const tableHeight = 522;
  const listItemType = 'CodeScheduleDKListItem';

  const totalDataCountRef = useRef(0);
  const headerOrderByText = useRef('');

  const { FilterButton, search } = useFilterBar(
    codesPortCodeList,
    undefined,
    getDefaultOrderBy(),
    String(componentIndex),
    false,
    setPageNum,
    {
      pageSize: pageSize,
      pagePerBatch: pagePerBatch,
    },
    setOrderBy,
  );

  function getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'code',
      orderByAscending: true,
    };
  }

  function setSdp(c:any) {
    setHasChanges(true);
    _setSdp(c);
  }

  function setData(d:Array<any>) {
    removeData();
    _setData(d);
  }

  async function fetchData(api: any, pageNum: number, forceCallDb: boolean, newItem: any = undefined) {
    setLoading(true);

    const prevPageNum = pageNumRef.current;
    const prevBlockStartPage = prevPageNum ? prevPageNum - ((prevPageNum - 1) % search.pagePerBatch) : null;
    const prevBlockNum = prevBlockStartPage ? Math.floor((prevBlockStartPage - 1) / search.pagePerBatch) : null;

    setPageNum(pageNum);

    const blockStartPage = pageNum - ((pageNum - 1) % search.pagePerBatch);
    const blockNum = Math.floor((blockStartPage - 1) / search.pagePerBatch);

    const isSameBlock = prevBlockNum === blockNum;
    const searchWithHeaderOrder = {
      ...search,
      headerOrderBy: headerOrderByText.current,
    };
    let entities: any[] = [];

    if (!isSameBlock || forceCallDb) {
      const apiResult = await api.getListItems(JobNames.OceanPort,
        listItemType,
        CodeScheduleDKListItem.calculateSearchParameters(searchWithHeaderOrder),
        blockStartPage,
        0,
        search.pageSize,
        search.pagePerBatch);

      entities = apiResult && apiResult.entities ? apiResult.entities : [];
      if (entities.length > 0) {
        if (newItem) {
          const filteredEntities = entities.filter(e => e.id !== newItem.id);

          let merged = [newItem, ...filteredEntities];

          const maxCount = search.pageSize * search.pagePerBatch;
          if (merged.length > maxCount) {
            merged = merged.slice(0, maxCount);
          }

          entities = merged;
        }
      }
      totalDataCountRef.current = apiResult?.totalCount ?? 0;
      setData(formatData(entities, pageNum, search.pageSize, search.pagePerBatch));
      
    }

    setLoading(false);
    setHasChanges(false);
  }

  useEffect(() => {
    if (isFilterObjectEmpty(search) && search.version === 1) reload();
    
    return () => {
      removeData();
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (loading || (isFilterObjectEmpty(search) && search.version === 1)) {
      return;
    }
    reload(true);
  },
    [search]
  );

  useEffect(() => {
    if (dataRef.current?.length > 0 && !loading && pageNum) {
      const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);
      const indexOffset = (pageNum - blockStartPage) * pageSize;
      setTimeout(() => {
        selectTableRow(indexOffset, true);
        onRowSelect(indexOffset);
      }, 10);
    }
  }, [loading, pageNum]);

  function updateHeaderOrderByText(orderBy: string, orderByAscending: boolean) {
    if (!orderBy) return;
    const orderStr = orderByAscending
      ? `${orderBy} ASC`
      : `${orderBy} DESC`;
    headerOrderByText.current = orderStr;
  }

  async function reload(clearHeaderOrder: boolean = true) {
    if (clearHeaderOrder) {
      if (isFilterObjectEmpty(search)) {
        headerOrderByText.current = '';
        setOrderBy(getDefaultOrderBy());
      }
      else {
        updateHeaderOrderByText(search.orderBy, search.orderByAscending);
        setOrderBy({ orderBy: search.orderBy, orderByAscending: search.orderByAscending });
      }
    }

    await fetchData(appContext.api, 1, true);
  }

  function onHeaderClick(header: any) {
    if (!pageNum) return;

    if (!orderBy || !orderBy.orderBy) return;

    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({ orderByAscending: !orderBy.orderByAscending });
      } else {
        setOrderBy({ orderBy: header.id, orderByAscending: true });
      }

      let newOrderStr = `${header.id} ASC`;

      if (orderBy && orderBy.orderBy === header.id) {
        newOrderStr = orderBy.orderByAscending ? `${header.id} ASC` : `${header.id} DESC`;
      }

      headerOrderByText.current = newOrderStr;
      fetchData(appContext.api, pageNum, true);
    }
  }

  function onRowDoubleSelect(rowIndex:number) {
    if (!dataRef || !dataRef.current) {
      return;
    }
    if (rowIndex >= 0 && rowIndex < dataRef.current.length && _.isEmpty(sdp)) {
      const objContainer = { ...dataRef.current[rowIndex], edit: true, view: true, editIndex: rowIndex};
      setSdp(objContainer);
    }
  }

  function onRowSelect(rowIndex:number) {
    setSelectedRow(!isNaN(rowIndex) ? rowIndex : -1 );
  }

  function selectForNew() {
    const newCodeModel = new CodeScheduleDKListItem();   
    setSdp(newCodeModel);
    
    setSdp({
      edit : false,
      view: false,
      isNew: true,
      code: '',
      portName: '',
      id: 0,
      cy: 0,
      cyName: '',
      cfs: 0,
      cfsName: '',
    });
  }

  function selectForEdit(){
    if (selectedRow >= 0 && selectedRow < dataRef.current.length && _.isEmpty(sdp)) {
      const objContainer = { ...dataRef.current[selectedRow], edit: true, view: false, editIndex: selectedRow};
      setSdp(objContainer);
    
    }
  }

  function selectForView(){
    if (selectedRow >= 0 && selectedRow < dataRef.current.length && _.isEmpty(sdp)) {
      const objContainer = { ...dataRef.current[selectedRow], edit: true, view: true, editIndex: selectedRow};
      setSdp(objContainer);
    }
  }

  async function selectForDelete(){
    const doConfirm = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    , false, 0, true, 'warning');
    if(doConfirm){
      const apiResult = await appContext.api.deleteJobDetail(JobNames.OceanPort, { id:dataRef.current[selectedRow].id , pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        await reload();
      } else
        showGlobalOkDialog(<p>{"Cannot delete"}</p>);
    } else
      return;
  }

  async function deleteEntity() {
    if (!sdp) {
      return;
    }

    const doDelete = await showGlobalYesNoDialog(<p>Are you sure you want to delete?</p>, false, 0, true, 'warning');
    if (!doDelete) {
      return;
    }

    const apiResult:ApiResult<InsertResult> = await appContext.api.saveListItem(listItemType,
      SQLType.Delete, _.omit(sdp, ['isNew']));
    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
      return;
    }

    cancelOperation();
    await reload();
  }

  function cancelOperation() {
    const filterNode = document.getElementById('ifms-list-screen-filter');
    const channel = postal.channel('ifms-filter');

    if (!filterNode) {
      setHasChanges(false);
      removeSdpFromStorage();
    } else {
      channel.publish('ifms-filter.dismiss');
    }
    return true;
  }

  async function checkDataAsync(): Promise<boolean>{
    if(!sdp) return false;

    if(sdp.code?.length === 0){
      await showGlobalOkDialog(<p>{"  * Empty Code"}</p>);
      return false;
    }
    else {
      if(sdp.isNew || hasChanges ){
        if (await appContext.api.checkupDuplicateFieldValueIsDuplicate("T_CODEOCEANPORT", "F_Code", sdp.code, 'F_Id', sdp.id)) {
          await showGlobalOkDialog(<p> * Duplicate Number in [Code]</p>);
          return false;
        }
      }
    }

    const comLks: CheckLookup[] = [];

    comLks.push(new CheckLookup({
      labelName: "CY Location",
      textValue: new TextValue({Text: sdp.cyName, IntValue: sdp.cy})
    }))
    comLks.push(new CheckLookup({
      labelName: "CFS Location",
      textValue: new TextValue({Text: sdp.cfsName, IntValue: sdp.cfs})
    }))

    const tempErrMsg = await appContext.api.checkCompanyLookupField(comLks);
    if (tempErrMsg) {
      await showGlobalOkDialog(tempErrMsg);
      return false;
    }

    return true;
  }

  async function save() {
    if (!sdp) {
      return;
    }

    if (!sdp.isNew && !hasChanges) {
      cancelOperation();
      return;
    }

    if (!await checkDataAsync()) return;

    const apiResult:ApiResult<InsertResult> = await appContext.api.saveListItem(listItemType,
      sdp.isNew ? SQLType.Insert : SQLType.Update,
      _.omit(sdp, ['isNew']));
    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
      return;
    }

    await showGlobalOkDialog(<p>The data has been saved</p>);
    if (apiResult.result) {
      const insresult = apiResult.result.newId;
      if (insresult) {
        const newItem: any = {
          id: insresult,
          code: sdp.code,
          portName: sdp.portName,
          cy: sdp.cy,
          cyName: sdp.cyName,
          cfs: sdp.cfs,
          cfsName: sdp.cfsName,
        };
        await fetchData(appContext.api, 1, true, newItem);
      }
    }
    else {
      const editItem = data.find((x: any) => x.id === sdp.id);
      editItem.code = sdp.code;
      editItem.portName = sdp.portName;
      editItem.cy = sdp.cy;
      editItem.cyName = sdp.cyName;
      editItem.cfs = sdp.cfs;
      editItem.cfsName = sdp.cfsName;

      const insIndex = data.findIndex((x: any) => x.id === sdp.id);
      const newList = _.cloneDeep(data);
      newList.splice(insIndex, 1, editItem);
      setData(formatData(newList, pageNum ?? 1, pageSize, pagePerBatch));
      selectTableRow(insIndex);
    }
    cancelOperation();
    dirtyCleanup();
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-sdk-list',
    selectable: true,
    tableName: 'sdktypeList',
    ...codesPortCodeList,
    height: `${tableHeight}px`,
    data: (data ?? []).map((x: any) => ({ ...x })),
    rowRenderers: [
      { accessor: 'indicator', Cell: TextRenderer },
      { accessor: 'code', Cell: TextAlignLeftRenderer },
      { accessor: 'portName', Cell: TextAlignLeftRenderer },
      { accessor: 'cyName', Cell: TextAlignLeftRenderer },
      { accessor: 'cfsName', Cell: TextAlignLeftRenderer },
    ],
    orderBy: [orderBy],
    onHeaderClick,
    paging: {
      pageSize: pageSize,
      pageCount: Math.ceil(totalDataCountRef.current / pageSize),
      pagePerBatch: pagePerBatch,
      onPageSelect: (page: number) => {
        fetchData(appContext.api, page, false);
      },
      totalDataCount: totalDataCountRef.current,
      pageNum,
      labelStyle: {
        hide: false,
        isFilterEmpty: isFilterObjectEmpty(search),
        emptyLabel: 'Total',
      },
    },
  };

  const hasSelectedRow = !isNaN(selectedRow) && !(selectedRow < 0);
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;

  return (
    <div style={{ marginTop:'40px'}}>
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', width: '100px' }}>
          <div style={{margin:'auto'}}></div>
          <div className="ifms-common-bar-element-center">
            <CloseButton onlyIcon name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>

      <div style={{display:'flex', width: '862px'}}>
        <div style={{marginLeft: 'auto'}}></div>
        <div style={{display:'flex', position: 'relative', top: '-4px' }}>
          <div style={{ display: 'none' }}>
            <FilterButton /> &nbsp;&nbsp;
          </div>
          <AddIconButton3 name='codes-scheduledk-add-button' disabled={!canBeAdd} onClick={selectForNew}/>&nbsp;&nbsp;
          <EditIconButton3 name='codes-scheduledk-edit-button' disabled={!canBeEdit} onClick={selectForEdit}/>&nbsp;&nbsp;
          <ViewIconButton3 name='codes-scheduledk-view-button' disabled={!hasSelectedRow} onClick={selectForView} iconHeight='26px' />&nbsp;&nbsp;
          <DeleteIconButton3 name='codes-scheduledk-delete-button' disabled={!canBeDeleted} onClick={selectForDelete}/>&nbsp;
          {/* <BlackInverseButton name='list-screen-refresh' size="large"  disabled={loading} onClick={() => {cancelOperation(); reload(true); }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton> */}
          <RefreshIconButton2 name='list-screen-refresh' onClick={() => {cancelOperation(); reload(true); }} disabled={loading} data-tooltip="Refresh"/>
        </div>  
      </div>

      <div style={{width: '862px'}}>
        <SmartTable {...tableOptions} />
      </div>
      <IfmsModal
        width="760px"
        isBlueHeader
        isOpen={!!sdp}
        title={sdp && sdp.edit ? (sdp.view ? 'Schedule D&K View' : 'Schedule D&K Edit') : 'Schedule D&K New'}
        onCancel={cancelOperation}
      >
        {!!sdp &&
          <div className="report-popup">
            <SdpDetails readonly={sdp.view || !canBeEdit} sdp={sdp} onChange={setSdp} />
            <div className='report-footer'>
              {canBeEdit  && !sdp.view &&
                  <SecondaryButton
                    name='scheduledk-detail-save-button' 
                    size="regular"
                    style={{width: '90px'}}
                    onClick={save}
                  >
                    SAVE
                  </SecondaryButton>
              }
              {canBeEdit  && sdp.view &&
                  <SecondaryButton
                    name='scheduledk-detail-edit-button'
                    size="regular"
                    style={{width: '90px'}}
                    onClick={()=>setSdp({edit:true, view:false})}
                  >
                    Edit
                  </SecondaryButton>
              }
              
              {!sdp.isNew && !sdp.view && listSec?.grantDel &&
                <SecondaryButton
                  name='scheduledk-detail-delete-button'
                  size="regular"
                  style={{width: '90px'}}
                  onClick={deleteEntity}
                >
                  DELETE
                </SecondaryButton>
              }
              <SecondaryButton
                name='scheduledk-detail-close-button'
                size="regular"
                style={{width: '90px'}}
                onClick={cancelOperation}>
                CANCEL
              </SecondaryButton>
            </div>
          </div>
        }
      </IfmsModal>
    </div>
  );
}

export default ScheduleDkPort;
